/**
 * Token search (Bez, 2026-10-03): whoever types a name, a symbol or a contract address gets a list to choose from, like DexScreener or
 * PancakeSwap. Pure logic, no network: parse the query, turn an outside index's pools into token rows, rank them, merge them with the
 * local list. The server (server/search.ts) feeds it; the client (components/SearchSuggest.tsx) shows it.
 */
import { GECKO_DEX_TO_NAME, isMajorToken, lc } from 'views/TradingCompetitionV3/pool-search/logic'
import type { GeckoPool } from 'views/TradingCompetitionV3/pool-search/logic'
import { HOT_MIN_LIQUIDITY_USD } from './list'
import type { Address, Cad1Tick, ListItem } from './types'

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/

export type SearchQuery = { mode: 'address'; address: Address; key: string } | { mode: 'text'; text: string; key: string } | { mode: 'invalid' }

/**
 * Any case, spaces and non-Latin letters are a text query; a full 0x address is an address; a partial 0x… is only filtered locally.
 * Text: 2 to 40 characters (one is enough when it is not ASCII, e.g. a CJK name), no markup or control characters.
 */
export function parseSearchQuery(raw: string): SearchQuery {
  const q = (raw ?? '').replace(/\s+/g, ' ').trim().replace(/^\$/, '').trim()
  if (ADDRESS_RE.test(q)) return { mode: 'address', address: q as Address, key: `a:${lc(q)}` }
  if (/^0x[0-9a-f]*$/i.test(q)) return { mode: 'invalid' }
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f<>{}"`\\;]/.test(q)) return { mode: 'invalid' }
  const chars = Array.from(q)
  const ascii = /^[ -~]*$/.test(q)
  if (chars.length > 40 || chars.length < (ascii ? 2 : 1)) return { mode: 'invalid' }
  return { mode: 'text', text: q, key: `t:${q.toLowerCase()}` }
}

export type PoolLine = { pool: Address; dex: string; pair: string; liquidityUsd: number | null }
export type SearchToken = {
  address: Address
  symbol: string
  name: string
  logoUrl: string | null
  /** the exchange of its deepest pool */
  bestDex: string | null
  /** summed over its pools on the supported exchanges */
  liquidityUsd: number | null
  /** at least one of its pools is confirmed by that exchange's factory on chain */
  verified: boolean
  /** below the anti-spam floor or no verified pool: shown, never hidden, but marked */
  lowLiquidity: boolean
  pools?: PoolLine[]
  /** the green CAD1 tick (the token is in a CAD1 launcher registry); absent when it is not */
  cad1?: Cad1Tick | null
}

const textMatch = (needle: string, symbol?: string, name?: string) => {
  const n = needle.toLowerCase()
  return (symbol ?? '').toLowerCase().startsWith(n) || (name ?? '').toLowerCase().startsWith(n) || (name ?? '').toLowerCase().includes(` ${n}`)
}

/** Exact symbol first, then exact name, then liquidity; ties by symbol. */
export function rankSearchTokens(tokens: SearchToken[], text: string): SearchToken[] {
  const t = text.trim().toLowerCase()
  const score = (x: SearchToken) => (x.symbol.toLowerCase() === t ? 0 : x.name.toLowerCase() === t ? 1 : 2)
  return [...tokens].sort((a, b) => score(a) - score(b) || (b.liquidityUsd ?? -1) - (a.liquidityUsd ?? -1) || a.symbol.localeCompare(b.symbol))
}

/**
 * The tokens of an index's pools that match the text: one row per token address (never a major), liquidity summed over its pools on the
 * supported exchanges, the deepest pool's exchange as `bestDex`, verified when any of its pools is in `verified` (lowercase pool addresses).
 */
export function tokensFromPools(pools: GeckoPool[], text: string, verified: Set<string>, floorUsd = HOT_MIN_LIQUIDITY_USD): SearchToken[] {
  const by = new Map<string, SearchToken & { _deepest: number }>()
  for (const p of pools) {
    const dex = GECKO_DEX_TO_NAME[lc(p.dexId)]
    if (!dex) continue
    const sides = [
      { address: p.baseToken, symbol: p.baseSymbol, name: p.baseName, logo: p.baseLogo, other: p.quoteSymbol },
      { address: p.quoteToken, symbol: p.quoteSymbol, name: p.quoteName, logo: p.quoteLogo, other: p.baseSymbol },
    ]
    for (const s of sides) {
      if (!ADDRESS_RE.test(s.address) || isMajorToken(s.address) || !s.symbol || !textMatch(text, s.symbol, s.name)) continue
      const key = lc(s.address)
      const it = by.get(key) ?? { address: s.address, symbol: s.symbol, name: s.name || s.symbol, logoUrl: null, bestDex: null, liquidityUsd: null, verified: false, lowLiquidity: true, pools: [], _deepest: -1 }
      by.set(key, it)
      if (!it.logoUrl && s.logo) it.logoUrl = s.logo
      if (p.liquidityUsd !== null) it.liquidityUsd = (it.liquidityUsd ?? 0) + p.liquidityUsd
      if ((p.liquidityUsd ?? 0) > it._deepest) {
        it._deepest = p.liquidityUsd ?? 0
        it.bestDex = dex
      }
      if (verified.has(lc(p.pool))) it.verified = true
      it.pools?.push({ pool: p.pool, dex, pair: `${s.symbol}/${s.other ?? '?'}`, liquidityUsd: p.liquidityUsd })
    }
  }
  const out = Array.from(by.values()).map(({ _deepest, ...t }) => ({
    ...t,
    lowLiquidity: !t.verified || (t.liquidityUsd ?? 0) < floorUsd,
    pools: (t.pools ?? []).sort((a, b) => (b.liquidityUsd ?? -1) - (a.liquidityUsd ?? -1)).slice(0, 3),
  }))
  return rankSearchTokens(out, text)
}

export type Suggestion = SearchToken & {
  source: 'list' | 'search'
  /** the token's one tick, read from the list (gold / blue); null when it has none or is not listed */
  tick: ListItem['verified']
  /** the token's registered page as the list knows it (verified / basic); null when it has none or is not listed */
  page: ListItem['page']
  /** the green CAD1 tick, from the list or the search row; null when the token is not a CAD1 launch */
  cad1: Cad1Tick | null
}

/**
 * Local list matches first, then the search rows not already listed; one row per address. A local row from the hot list is vetted
 * (CADINUdex or pinned: `verified`); a local row that is only a basic page (Bez, 2026-10-08: every token with a page is findable) is not,
 * and is marked as a basic page instead. Every row carries the token's tick and page from the list: `known` is the whole list, so a listed
 * token that arrives as a search row still shows them.
 */
export function mergeSuggestions(local: ListItem[], remote: SearchToken[], limit = 8, known: ListItem[] = local): Suggestion[] {
  const flags = new Map(known.map((i) => [lc(i.address), { tick: i.verified ?? null, page: i.page ?? null, cad1: i.cad1 ?? null }]))
  const seen = new Set<string>()
  const out: Suggestion[] = []
  for (const i of local) {
    if (seen.has(lc(i.address))) continue
    seen.add(lc(i.address))
    const basicOnly = i.page === 'basic' && i.hotRank === null
    out.push({ address: i.address, symbol: i.symbol, name: i.name, logoUrl: i.logoUrl, bestDex: i.dexNames[0] ?? null, liquidityUsd: i.liquidityUsd, verified: !basicOnly, lowLiquidity: false, source: 'list', tick: i.verified ?? null, page: i.page ?? null, cad1: i.cad1 ?? null })
  }
  for (const r of remote) {
    if (seen.has(lc(r.address))) continue
    seen.add(lc(r.address))
    const f = flags.get(lc(r.address))
    out.push({ ...r, source: 'search', tick: f?.tick ?? null, page: f?.page ?? null, cad1: f?.cad1 ?? r.cad1 ?? null })
  }
  return out.slice(0, limit)
}

export type TokenSearchResult = {
  query: string
  mode: 'address' | 'text'
  tokens: SearchToken[]
  /** an address that answers no ERC-20 name, symbol or decimals */
  notToken?: boolean
  /** no outside index answered (GeckoTerminal and DexScreener both rate-limited or down) */
  busy: boolean
  sources: { gecko: string; dexscreener: string; verify: 'ok' | 'unavailable' }
  builtAt: number
}
